/* eslint-disable max-statements */
import iconMap from 'LocalGluons/components/Icon/iconMap';
import {
    filterOutAdultItemsByCategories,
    isContentBlocked,
    isContentHidden,
    isContentLocked
} from 'Utils/parentalControlUtils';
import { getTvTitle } from 'Utils';
import { ProgramModel } from '@24i/st-api-connector';
import { DEFAULT_LIST_MODE } from '../options';

const INITIAL_EPG_OFFSET = 0;
const DEFAULT_EPG_LIMIT = 20;
const SAFE_EPG_LIMIT = 100;
const EPG_FETCH_STATE = {
    LOADING: 0,
    DONE: 1,
    ERROR: 2,
    END_REACHED: 3 // when there are no more items
};

const getDateInterval = (date) => {
    const start = date ? new Date(date) : new Date();
    start.setDate(start.getDate() - 1);
    start.setHours(23, 0, 0, 0);
    const end = date ? new Date(date) : new Date();
    end.setHours(23, 59, 59, 999);

    return {
        from: start.getTime(),
        to: end.getTime()
    };
};

const dateDiffInWords = (first, second) => {
    // Discard the time and time-zone information.
    const utc1 = Date.UTC(first.getFullYear(), first.getMonth(), first.getDate());
    const utc2 = Date.UTC(second.getFullYear(), second.getMonth(), second.getDate());
    const dateDiff = Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24));
    if (dateDiff === 0) {
        return 'TODAY';
    }
    if (dateDiff === 1) {
        return 'TOMORROW';
    }
    if (dateDiff === -1) {
        return 'YESTERDAY';
    }
    if (dateDiff < -1) {
        return `${Math.abs(dateDiff)} DAYS AGO`;
    }
    if (dateDiff > 1) {
        return `IN ${dateDiff} DAYS`;
    }

    return '';
};

const extendEpgItems = (
    items,
    recProgramIds,
    notifications,
    parentalLockConfig,
    sessionConfig,
    categories
) =>
    items.map((item) => {
        const extendedPrograms = [];
        // Extend program with icons representing its state
        item.programs.forEach((program) => {
            const programId = program.getProgramId();

            if (!program.data) program.data = {};

            program.data.icons = [];
            program.data.iconsAsString = [];
            program.data.isLocked = false;

            // patchy fix for missing config on item
            program.config = sessionConfig;

            if (program.getScheduleStatus() === 'archive' && item.channel.hasArchive) {
                program.data.icons.push(iconMap.archive);
                program.data.iconsAsString.push('archive');
            }

            if (notifications[program.getId()] !== undefined) {
                program.data.icons.push(iconMap.alarm);
                program.data.iconsAsString.push('alarm');
            }

            recProgramIds.forEach((recId) => {
                // Is somewhere in recording series
                if (recId instanceof Array && recId.indexOf(programId) >= 0) {
                    program.data.icons.push(iconMap.record_series);
                    program.data.iconsAsString.push('record_series');
                }
                if (recId === programId) {
                    program.data.icons.push(iconMap.player_record_on);
                    program.data.iconsAsString.push('player_record_on');
                }
            });

            const isAdult = program.isAdult();

            // If content should be hidden from EPG
            if (isContentHidden(isAdult, parentalLockConfig)) return;

            program.data.isLocked = isContentLocked(isAdult, parentalLockConfig);
            program.data.isAgeBlocked = isContentBlocked(
                program.data?.parentalRating,
                parentalLockConfig
            );

            if (program.data.isLocked || program.data.isAgeBlocked) {
                program.data.icons.push(iconMap.lock);
                program.data.iconsAsString.push('lock');
            }

            // If the item is returned back that means it is not filtered, so not adult
            const filtered = filterOutAdultItemsByCategories(
                [program],
                categories,
                parentalLockConfig
            );
            if (filtered && filtered.length > 0) {
                extendedPrograms.push(program);
            }
        });

        item.programs = extendedPrograms;

        return item;
    });

export const filterCategoriesChannelsByChannels = (categories, channels) => {
    if (!channels || channels.length === 0) {
        return categories;
    }

    const channelIds = channels.map((ch) => ch.channelId);
    return categories?.map((cat) => ({
        ...cat,
        channels: cat.channels.filter((ch) => channelIds.includes(ch.channelId))
    }));
};

export const replaceEpgNames = (items) =>
    items.forEach((item) => {
        const newPrograms = [];
        // Break reference like this
        item.programs.forEach((program) => {
            const newData = { ...program.data };
            newData.name = getTvTitle(program);
            newPrograms.push(new ProgramModel(newData, program.locale, program.config));
        });
        item.programs = newPrograms;
    });

export const orderItemsByFavChannelOrder = (
    epgItems = [],
    favoriteChannels = [],
    filterOutNonFavorites = false
) => {
    const ordered = [];
    const rest = [...epgItems];
    favoriteChannels.forEach(({ channelId }) => {
        const matchingIndex = epgItems.findIndex(
            (epgItem) => epgItem.channel.channelId === channelId
        );
        if (matchingIndex !== -1) {
            const match = epgItems[matchingIndex];
            ordered.push(match);
            const indexToRemove = rest.findIndex(
                (item) => item.channel.channelId === match.channel.channelId
            );
            rest.splice(indexToRemove, 1);
        }
    });

    return [...ordered, ...(filterOutNonFavorites ? [] : rest)];
};

export const areFavoritesEqual = (previous, current, checkOrder = true) => {
    if (previous.length !== current.length) return false;
    const sameItems = previous.filter((item, previousIndex) =>
        current.find((currentItem, currentIndex) => {
            const sameChannelIdFound = item.channelId === currentItem.channelId;
            return checkOrder
                ? sameChannelIdFound && previousIndex === currentIndex
                : sameChannelIdFound;
        })
    );
    return sameItems.length === previous.length;
};

export const customFilterAdapter = (filters, data) => {
    let filteredData = data;
    for (let filterIndex = 0; filterIndex < filters.length; filterIndex++) {
        for (let i = 0; i < filteredData.length; i++) {
            if (
                filters[filterIndex].key !== 'date' &&
                filters[filterIndex].value.some(
                    // TODO: refactor
                    // eslint-disable-next-line no-loop-func
                    (val) => filteredData[i].channel[filters[filterIndex].key].has(val)
                )
            ) {
                // for now date filtering is handled by the epg
                filteredData = filteredData.filter((el) =>
                    filters[filterIndex].value.some((val) =>
                        el.channel[filters[filterIndex].key].has(val)
                    )
                );
            }
        }
    }
    return filteredData;
};

export const getEpgMaxDaysBack = (mCoreConfig) =>
    // eslint-disable-next-line no-unsafe-optional-chaining
    Math.round(mCoreConfig?.archiveSchedulesValidInMillis / (24 * 60 * 60 * 1000));

export default {
    INITIAL_EPG_OFFSET,
    DEFAULT_EPG_LIMIT,
    SAFE_EPG_LIMIT,
    EPG_FETCH_STATE,
    DEFAULT_LIST_MODE,
    getDateInterval,
    dateDiffInWords,
    extendEpgItems,
    orderItemsByFavChannelOrder,
    areFavoritesEqual,
    customFilterAdapter,
    getEpgMaxDaysBack,
    replaceEpgNames
};
